import {
	ChildToAdd,
	RelationshipType,
	TitleAddChildrenMutation,
	TitleAddChildrenMutationOptions,
} from '@warehouse/title/core';
import { CompilationObjectEntry } from '@nexspec/warehouse-shared-types/dist/titles/TitleMetadata/TitleMetadata';
import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { AddParentsResult } from '@warehouse/graphql';
import { useEditTitleV2 } from '../../../src/utils/hooks/titles/useEditTitle';
import useWorkTypes from '../../../src/utils/hooks/titles/useWorkTypes';

import { useAddParents } from '../../../src/utils/hooks/titles/useAddParents';
import { titleRepositorySingleton } from './title.repository.gql';
import { getWorkTypeObject, manifestationUuid } from './worktype';
import { ContentRelatedWork } from '../core/models/content-relatedto';

export function useAddChildrenMutationImpl({
	options,
}: {
	options?: TitleAddChildrenMutationOptions;
}): ReturnType<TitleAddChildrenMutation> {
	const { compilationUuid } = useWorkTypes();
	const compilationAddChildren = useCompilationAddChildrenMutation(options);
	const titleAddChildren = useTitleAddChildrenMutation(options);
	const contentRelatedAddChildren = useContentRelatedAddChildrenMutation(options);

	const allowAddingEntries = (workTypeUuid: string) =>
		workTypeUuid === compilationUuid() || workTypeUuid === manifestationUuid();

	const mutateHandler = ({
		workTypeUuid,
		titleUuid,
		children,
		relationshipType,
	}: {
		titleUuid: string;
		children: ChildToAdd[];
		workTypeUuid: string;
		relationshipType?: string;
	}) => {
		if (relationshipType === RelationshipType.RelatedTo) {
			return contentRelatedAddChildren.mutate(titleUuid, children);
		}
		if (allowAddingEntries(workTypeUuid) && relationshipType === RelationshipType.EntryOf) {
			return compilationAddChildren.mutate(titleUuid, children);
		}
		return titleAddChildren.mutate(titleUuid, children, relationshipType ?? undefined);
	};

	return {
		error: compilationAddChildren.error || titleAddChildren.error,
		loading: compilationAddChildren.loading || titleAddChildren.loading,
		mutate: mutateHandler,
	};
}

function useCompilationAddChildrenMutation(options?: TitleAddChildrenMutationOptions) {
	const [getTitleLoading, setGetTitleLoading] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const addChildrenMutation = useEditTitleV2({
		onCompleted: () => {
			enqueueSnackbar('Children added successfully', {
				variant: 'success',
			});
			options?.onCompleted?.();
		},
		onError: () => {
			enqueueSnackbar('Failed to add children', {
				variant: 'error',
			});
		},
	});
	const repo = titleRepositorySingleton.get();

	return {
		loading: addChildrenMutation.isPending || getTitleLoading,
		mutate: async (titleUuid: string, children: ChildToAdd[]) => {
			setGetTitleLoading(true);
			const result = await repo.getTitle(titleUuid);
			setGetTitleLoading(false);
			const existingCompilationEntries = result.metadata.coreMetadata.compilationObject?.compilationEntries;

			return addChildrenMutation.mutate({
				editTitleInput: {
					edits: [
						{
							path: 'coreMetadata.compilationObject.compilationEntries',
							value: JSON.stringify(
								makeCompilationEntriesUnique([
									...(existingCompilationEntries || []),
									...childrenToCompilationEntries(children),
								]),
							),
						},
					],
					type: 'explicit',
					uuid: titleUuid,
				},
			});
		},
		error: addChildrenMutation.error,
	};
}

function useTitleAddChildrenMutation(options?: TitleAddChildrenMutationOptions) {
	const { enqueueSnackbar } = useSnackbar();
	const [addParents, { loading, error }] = useAddParents({
		onSuccess: (successfulResults: AddParentsResult[]) => {
			const count = successfulResults.length;
			const childWord = count === 1 ? 'child' : 'children';
			enqueueSnackbar(`${count} ${childWord} added successfully`, {
				variant: 'success',
			});
			options?.onCompleted?.();
		},
		onError: (singleError) => {
			enqueueSnackbar(singleError, {
				variant: 'error',
			});
		},
	});

	return {
		loading,
		mutate: async (titleUuid: string, children: ChildToAdd[], relationshipType?: string) => {
			if (!relationshipType) throw new Error('RelationshipType should be provided for non-compilation parents');
			const relationships = children.map(({ uuid: childUuid }) => ({
				parentUuid: titleUuid,
				childUuid,
				relationshipType,
			}));

			return addParents({
				variables: {
					input: {
						relationships,
					},
				},
			});
		},
		error,
	};
}

function useContentRelatedAddChildrenMutation(options?: TitleAddChildrenMutationOptions) {
	const [getTitleLoading, setGetTitleLoading] = useState(false);
	const { enqueueSnackbar } = useSnackbar();
	const addChildrenMutation = useEditTitleV2({
		onCompleted: () => {
			enqueueSnackbar('Children added successfully', {
				variant: 'success',
			});
			options?.onCompleted?.();
		},
		onError: () => {
			enqueueSnackbar('Failed to add children', {
				variant: 'error',
			});
		},
	});
	const repo = titleRepositorySingleton.get();

	return {
		loading: addChildrenMutation.isPending || getTitleLoading,
		mutate: async (titleUuid: string, children: ChildToAdd[]) => {
			setGetTitleLoading(true);
			const result = await repo.getTitle(titleUuid);
			setGetTitleLoading(false);
			/* 
      For now we only support one contentRelatedTo entry, so we take the first one if it exists, 
      otherwise we start with an empty array.
      as currently If we send to backend array with more than one contentRelatedTo entry, it will fail validation and return an error, 
      so we need to make sure to not send more than one entry.
      */
			const existingContentRelatedWork = result.metadata.coreMetadata.basic.contentRelatedTo?.[0].work || [];
			return addChildrenMutation.mutate({
				editTitleInput: {
					edits: [
						{
							path: 'coreMetadata.basic.contentRelatedTo',
							value: JSON.stringify([
								{
									relationship: {
										type: RelationshipType.RelatedTo,
									},
									work: makeContentRelatedWorkUnique([
										...(existingContentRelatedWork || []),
										...childrenToContentRelatedWork(children),
									]),
								},
							]),
						},
					],
					type: 'explicit',
					uuid: titleUuid,
				},
			});
		},
		error: addChildrenMutation.error,
	};
}

function makeCompilationEntriesUnique(compilationEntries: CompilationObjectEntry[]) {
	return compilationEntries.filter(
		(compilationEntry, index, self) =>
			self.findIndex((entry) => entry.contentId === compilationEntry.contentId) === index,
	);
}

function childrenToCompilationEntries(children: ChildToAdd[]) {
	return children.map(({ uuid, workType }) => ({
		contentId: uuid,
		compilationEntryClass: workType ? getWorkTypeObject(workType).uuid : undefined,
	}));
}

function makeContentRelatedWorkUnique(works: ContentRelatedWork[]) {
	return works.filter((work, index, self) => self.findIndex((w) => w.contentId === work.contentId) === index);
}

function childrenToContentRelatedWork(children: ChildToAdd[]) {
	return children.map(({ uuid }) => ({
		contentId: uuid,
	}));
}
